import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useRunTriggers from '../hooks/useRunTriggers';
import { useNavigate, useParams } from 'react-router-dom';
import { MapContainer, Polyline, useMap, useMapEvents } from 'react-leaflet';
import L, { LatLngExpression, LatLngTuple } from 'leaflet';
import polyline from '@mapbox/polyline';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../contexts/AppContext';
import Header from '../framework/Header';
import PageTransition from '../components/PageTransition';
import ErrorMessageView from '../components/ErrorMessage';
import MapMarker from '../components/MapMarker';
import MapMarkerPopup from '../components/MapMarkerPopup';
import useFetchDocument from '../hooks/useFetchDocument';
import { getTranslation } from '../common/Translation';
import { usePlausiblePageview } from '../hooks/usePlausibleTracker';
import { API_PREFIX, MAP_API_KEY, MAP_ROUTING_MACHINE_URL, MAP_TILE_RASTER_URL, MEDIA_PREFIX } from '../Settings';
import 'leaflet/dist/leaflet.css';

type RoutingMode = "route" | "lines" | "off";

interface SelectedMarker {
  documentId: number,
  latLng: LatLngTuple,
  title?: any,
  subtitle?: any,
  image?: any,
  notClickable?: boolean
}

const MapWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;

  .leaflet-container {
    width: 100%;
    height: 100%;
    background: #dce3ea;
    font: inherit;
  }

  .map-marker-icon {
    background: transparent !important;
    border: none !important;
    overflow: visible !important;
  }

  .map-marker-pin {
    line-height: 0;
  }

  .map-marker-icon.transparent .map-marker-pin {
    opacity: 0;
  }

  .map-marker-pin.circle {
    filter: drop-shadow(1px 2px 2px rgb(0 0 0 / 0.35));
  }

  /* Leaflet forces marker-pane imgs to width:auto — restore cover fill for popups */
  .imagemappopup img,
  .imagemappopup video {
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
  }

  .leaflet-control-attribution {
    display: none;
  }
`;

interface MapViewProps {
  previewData?: any,
  overrideTitle?: string
}

/** Extra panning room beyond outermost markers (meters per side) */
const MAP_BOUNDS_PADDING_METERS = 300;

/**
 * Build a Stadia raster tile URL, prefixed for client-side media caching.
 * @param tileset Stadia tileset id (e.g. alidade_smooth)
 * @returns Leaflet tile URL template
 */
const buildTileUrl = (tileset: string) => {
  const path = MAP_TILE_RASTER_URL
    .replace('{tileset}', tileset)
    .replace('{r}', '');

  return `${MEDIA_PREFIX}${path}?api_key=${MAP_API_KEY}`;
};

/**
 * Short unique id for a tile. The desktop/iOS cache keys on the filename (e.g. 35438.png)
 * plus query string, so this value must differ for every tileset/z/x/y combination.
 * @param tileset Stadia tileset id
 * @param coords Leaflet tile coordinates
 * @returns Base36 FNV-1a hash
 */
const tileCacheId = (tileset: string, coords: L.Coords) => {
  const key = `${tileset}/${coords.z}/${coords.x}/${coords.y}`;
  let hash = 2166136261;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

/**
 * Raster tiles with a short cache-busting query value unique per tile.
 */
const CacheSafeTileLayer: FC<{ tileset: string }> = ({ tileset }) => {
  const map = useMap();

  useEffect(() => {
    const layer = L.tileLayer(buildTileUrl(tileset), { attribution: "" });
    const getTileUrl = layer.getTileUrl.bind(layer);
    layer.getTileUrl = (coords) => `${getTileUrl(coords)}&a=${tileCacheId(tileset, coords)}`;
    map.addLayer(layer);
    return () => { map.removeLayer(layer); };
  }, [map, tileset]);

  return null;
};

/**
 * Resolve routing drawing mode from CMS settings.
 * @param value Raw settings.map.routing value
 * @returns Normalized routing mode
 */
const resolveRoutingMode = (value: unknown): RoutingMode => {
  if (value === "lines") { return "lines"; }
  if (value === "route" || value === true) { return "route"; }
  return "off";
};

/**
 * Expand marker bounds by a fixed distance on each side (default 1 km).
 * @param positions Known marker coordinates
 * @param paddingMeters Padding beyond outermost points
 * @returns Bounds or null when no positions exist
 */
const resolveMaxBounds = (
  positions: LatLngTuple[],
  paddingMeters = MAP_BOUNDS_PADDING_METERS
): L.LatLngBounds | null => {
  if (!positions.length) {
    return null;
  }

  const bounds = L.latLngBounds(positions);
  const sw = L.latLng(bounds.getSouth(), bounds.getWest()).toBounds(paddingMeters * 2);
  const ne = L.latLng(bounds.getNorth(), bounds.getEast()).toBounds(paddingMeters * 2);

  return L.latLngBounds(sw.getSouthWest(), ne.getNorthEast());
};

/**
 * Fit markers and refresh maxBounds after positions settle.
 * Debounced so incremental marker registration (and route drawing) does not
 * repeatedly jump the viewport.
 */
const MapViewportController: FC<{
  positions: LatLngTuple[],
  maxBounds: L.LatLngBounds | null
}> = ({ positions, maxBounds }) => {
  const map = useMap();
  const appliedKey = useRef<string | null>(null);
  const positionsKey = positions.map((point) => point.join(",")).join("|");

  useEffect(() => {
    if (!positions.length || !maxBounds?.isValid()) {
      return;
    }

    if (appliedKey.current === positionsKey) {
      return;
    }

    // Wait until marker locations stop arriving before fitting once
    const timer = window.setTimeout(() => {
      if (appliedKey.current === positionsKey) {
        return;
      }

      // Drop old constraints so fitBounds can include newly added points
      map.setMaxBounds(null as unknown as L.LatLngBounds);
      map.setMinZoom(1);

      map.fitBounds(L.latLngBounds(positions), {
        padding: [80, 80],
        maxZoom: 14,
        animate: false
      });

      map.options.maxBoundsViscosity = 1;
      map.setMaxBounds(maxBounds);
      map.setMinZoom(map.getBoundsZoom(maxBounds, false));
      appliedKey.current = positionsKey;
    }, 500);

    return () => window.clearTimeout(timer);
  }, [map, positions, positionsKey, maxBounds]);

  return null;
};

/**
 * Draw connections between markers: routed path or straight lines.
 */
const MapConnections: FC<{
  positions: LatLngTuple[],
  mode: RoutingMode,
  color?: string,
  costing?: string
}> = ({
  positions,
  mode,
  color = "#3F7DF0",
  costing = "pedestrian"
}) => {
  const [routePositions, setRoutePositions] = useState<LatLngExpression[]>([]);
  const positionsKey = positions.map((point) => point.join(",")).join("|");

  useEffect(() => {
    if (mode !== "route" || positions.length < 2) {
      setRoutePositions([]);
      return;
    }

    const controller = new AbortController();

    const fetchRoute = async () => {
      try {
        const response = await fetch(`${API_PREFIX}${MAP_ROUTING_MACHINE_URL}?api_key=${MAP_API_KEY}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
          body: JSON.stringify({
            locations: positions.map(([lat, lng]) => ({ lat, lon: lng, type: "break" })),
            costing,
            units: "kilometers"
          })
        });

        if (!response.ok) {
          throw new Error(`Routing request failed (${response.status})`);
        }

        const json = await response.json();
        const shapes: string[] = json?.trip?.legs?.map((leg: any) => leg.shape).filter(Boolean) || [];

        if (!shapes.length) {
          setRoutePositions(positions);
          return;
        }

        const decoded: LatLngExpression[] = [];
        shapes.forEach((shape) => {
          polyline.decode(shape, 6).forEach(([lat, lng]) => {
            decoded.push([lat, lng]);
          });
        });

        setRoutePositions(decoded.length ? decoded : positions);
      } catch (error: any) {
        if (error?.name === "AbortError") { return; }
        setRoutePositions(positions);
      }
    };

    fetchRoute();
    return () => controller.abort();
  }, [positions, positionsKey, costing, mode]);

  if (mode === "off" || positions.length < 2) {
    return null;
  }

  const linePositions = mode === "lines" ? positions : routePositions;
  if (linePositions.length < 2) {
    return null;
  }

  return (
    <Polyline
      positions={linePositions}
      pathOptions={{
        color,
        weight: 5,
        opacity: 0.85,
        lineJoin: "round",
        lineCap: "round",
        interactive: false
      }}
    />
  );
};

/**
 * Close open marker popup when the map background is clicked.
 */
const MapClickHandler: FC<{ onMapClick: () => void }> = ({ onMapClick }) => {
  useMapEvents({
    click: () => {
      onMapClick();
    }
  });

  return null;
};

/**
 * Render a geographic map presentation with markers from linked records.
 * @returns {JSX.Element} Component template
 */
const MapView: FC<MapViewProps> = ({ previewData = null, overrideTitle }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { documentId } = useParams<{ documentId: string }>();
  const { deviceId, currentLanguage, previewIsActive } = useContext(AppContext);
  const [data, error] = useFetchDocument(documentId, previewData);
  const [locations, setLocations] = useState<Record<number, LatLngTuple>>({});
  const [selected, setSelected] = useState<SelectedMarker | null>(null);
  const runTriggers = useRunTriggers();

  usePlausiblePageview(
    {
      deviceId: String(deviceId),
      documentId: documentId,
      documentType: 'map',
      ownerId: data?.owner_id,
      ownerSlug: data?.owner_slug,
      language: currentLanguage
    },
    { waitFor: ['ownerSlug'] }
  );

  const settings = data?.content?.settings;
  const tileset = settings?.map?.tileset || 'outdoors';
  const markerColor = settings?.marker?.markerColor || "#3F7DF0";
  const markerStyle = settings?.marker?.markerStyle;
  const routingMode = resolveRoutingMode(settings?.map?.routing ?? settings?.routing);
  const routingCosting = settings?.map?.routingProfile || settings?.routingProfile || "pedestrian";
  const recordList = data?.content?.records?.list || [];

  const orderedPositions = useMemo(() => {
    return recordList
      .map((item: any) => locations[item?.to_document_id])
      .filter(Boolean) as LatLngTuple[];
  }, [recordList, locations]);

  const maxBounds = useMemo(
    () => resolveMaxBounds(orderedPositions),
    [orderedPositions]
  );

  // Drop coordinates for records removed in preview / CMS updates
  useEffect(() => {
    const activeIds = new Set(
      recordList.map((item: any) => item?.to_document_id).filter(Boolean)
    );

    setLocations(prev => {
      let changed = false;
      const next: Record<number, LatLngTuple> = {};

      Object.entries(prev).forEach(([id, latLng]) => {
        if (activeIds.has(Number(id))) {
          next[Number(id)] = latLng;
        } else {
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [recordList]);

  /**
   * Track marker coordinates for fitBounds, routing and maxBounds.
   */
  const handleLocation = useCallback((id: number, latLng: LatLngTuple | null) => {
    setLocations(prev => {
      if (!latLng) {
        if (!(id in prev)) { return prev; }
        const next = { ...prev };
        delete next[id];
        return next;
      }

      const existing = prev[id];
      if (existing && existing[0] === latLng[0] && existing[1] === latLng[1]) {
        return prev;
      }

      return { ...prev, [id]: latLng };
    });
  }, []);

  /**
   * Open popup on first tap; follow the popup link on a second tap of the same marker.
   */
  const handleSelect = useCallback((id: number, latLng: LatLngTuple, contentType?: string, item?: any) => {
    if (selected?.documentId === id) {
      if (previewIsActive || item?.content?.notClickable || !deviceId) { return; }
      const type = contentType || "article";
      navigate(`/device/${deviceId}/${type}/${id}`);
      return;
    }

    // Run triggers when the marker is tapped to open its popup
    runTriggers(item?.content?.useTriggers, item?.content?.triggers);

    window.setTimeout(() => {
      setSelected({
        documentId: id,
        latLng,
        title: item?.content?.title,
        subtitle: item?.content?.subtitle,
        image: item?.content?.image,
        notClickable: Boolean(item?.content?.notClickable)
      });
    }, 0);
  }, [deviceId, navigate, previewIsActive, runTriggers, selected?.documentId]);

  const markers = recordList.map((item: any, i: number) => (
    <MapMarker
      key={`map-marker-${item?.to_document_id || i}`}
      documentId={item?.to_document_id}
      image={item?.content?.image}
      title={item?.content?.title}
      subtitle={item?.content?.subtitle}
      markerColor={markerColor}
      markerStyle={markerStyle}
      transparent={Boolean(item?.content?.transparent)}
      fallbackLocation={item?.content?.location || item?.location}
      onLocation={handleLocation}
      onSelect={(id, latLng, contentType) => handleSelect(id, latLng, contentType, item)}
    />
  ));

  if (error) {
    return <ErrorMessageView title={t("presentationNotFound.title")} body={t("presentationNotFound.body")} redirect={true} />;
  }

  if (data?.status && data?.status !== "published") {
    return <ErrorMessageView title={t("presentationNotPublished.title")} body={t("presentationNotPublished.body")} redirect={true} />;
  }

  return (
    <>
      <Header
        title={getTranslation(overrideTitle, currentLanguage) || (data?.content?.general?.showTitle ? getTranslation(data?.content?.general?.title, currentLanguage) : "")}
        backgroundColor={data?.content?.background?.backgroundColor}
      />
      <PageTransition waitFor={data}>
        <MapWrapper>
          {(data?.content) && (
            <MapContainer
              center={[64.5, 11.5]}
              zoom={5}
              minZoom={3}
              maxZoom={18}
              maxBoundsViscosity={1}
              inertia={false}
              scrollWheelZoom={true}
              zoomControl={false}
              attributionControl={false}
            >
              <CacheSafeTileLayer tileset={tileset} />
              <MapClickHandler onMapClick={() => setSelected(null)} />
              <MapViewportController positions={orderedPositions} maxBounds={maxBounds} />
              {routingMode !== "off" && (
                <MapConnections
                  positions={orderedPositions}
                  mode={routingMode}
                  color={markerColor}
                  costing={routingCosting}
                />
              )}
              {markers}
              {selected && (
                <MapMarkerPopup
                  documentId={selected.documentId}
                  latLng={selected.latLng}
                  title={selected.title}
                  subtitle={selected.subtitle}
                  image={selected.image}
                  markerColor={markerColor}
                  markerStyle={markerStyle}
                  onClose={() => setSelected(null)}
                  notClickable={selected.notClickable}
                />
              )}
            </MapContainer>
          )}
        </MapWrapper>
      </PageTransition>
    </>
  );
};

export default MapView;
